import {
  KeyIcon,
  LockIcon,
  PencilSimpleIcon,
  ShieldCheckIcon,
  TrashIcon,
} from '@phosphor-icons/react';
import { useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { DeleteRoleDrawer } from '@/components/admin/DeleteRoleDrawer';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { ToastNotification } from '@/components/ui/ToastNotification';
import { ManageRolesContent } from '@/content/ManageRolesContent';
import { useDeleteRole, useRole } from '@/lib/hooks/useRoles';
import { useToastNotification } from '@/lib/hooks/useToastNotification';
import type { RoleDetailProps } from '@/lib/types/roles.types';
import { cn } from '@/lib/utils';
import { getRoleColorTone } from '@/lib/utils/roles.utils';

export function RoleDetail({ roleId }: RoleDetailProps) {
  const navigate = useNavigate();
  const { data: role, isLoading, error } = useRole(roleId);
  const deleteRoleMutation = useDeleteRole();
  const { toast, showToast, hideToast } = useToastNotification();
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);

  const handleDeleteConfirm = async () => {
    if (!role) {
      return;
    }

    const roleName = role.display_name;

    try {
      await deleteRoleMutation.mutateAsync(role.id);
      setIsDeleteOpen(false);
      navigate({
        to: '/admin/manage-roles',
        search: { deletedRole: roleName },
      });
    } catch (deleteError) {
      setIsDeleteOpen(false);
      showToast({
        title: ManageRolesContent.toast.deleteErrorTitle,
        description:
          deleteError instanceof Error
            ? deleteError.message
            : 'Failed to delete role.',
        variant: 'error',
      });
    }
  };

  if (isLoading) {
    return (
      <div className="bg-slate-700">
        <div className="mx-auto flex w-full max-w-4xl flex-col px-4 pb-8 pt-3">
          <div className="space-y-4">
            <Skeleton className="h-8 w-52 rounded-full bg-slate-600" />
            <Skeleton className="h-20 w-full rounded-lg bg-slate-600" />
            <Skeleton className="h-32 w-full rounded-lg bg-slate-600" />
          </div>
        </div>
      </div>
    );
  }

  if (error || !role) {
    return (
      <div className="bg-slate-700">
        <div className="mx-auto flex w-full max-w-4xl flex-col px-4 pb-8 pt-3">
          <div className="rounded-lg border border-red-400 bg-red-500/10 px-4 py-6 text-center text-body text-red-100">
            {error instanceof Error
              ? error.message
              : ManageRolesContent.detail.notFound}
          </div>
        </div>
      </div>
    );
  }

  const tone = getRoleColorTone(role.chip_color);
  const status = role.is_system
    ? ManageRolesContent.detail.systemStatus
    : ManageRolesContent.detail.customStatus;
  const statusDescription = role.is_system
    ? ManageRolesContent.detail.systemDescription
    : ManageRolesContent.detail.customDescription;

  return (
    <div className="bg-slate-700">
      <div className="mx-auto flex w-full max-w-4xl flex-col px-4 pb-8 pt-3">
        <div className="mb-5">
          <div className="mb-3 flex items-center gap-2">
            <span
              aria-hidden="true"
              className={cn('h-3 w-3 rounded-full', tone.dotClassName)}
            />
            <span
              className={cn(
                'inline-flex items-center gap-1 rounded-full border bg-slate-600 px-2 py-0.5 font-field-42 text-[10px] uppercase',
                tone.pillClassName
              )}
            >
              {tone.label}
            </span>
            {role.is_system && (
              <span className="inline-flex items-center gap-1 rounded-full border border-blue-400/40 bg-blue-400/15 px-2 py-0.5 font-field-42 text-[10px] uppercase text-blue-200">
                <LockIcon size={11} weight="bold" />
                {ManageRolesContent.badges.system}
              </span>
            )}
          </div>
          <h1 className="font-field-74 text-h1 leading-tight text-slate-50">
            {role.display_name}
          </h1>
          {!role.is_system && (
            <div className="mt-4 flex flex-wrap gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() =>
                  navigate({
                    to: '/admin/manage-roles/$roleId/edit',
                    params: { roleId: role.id },
                  })
                }
                className="border-slate-550 bg-transparent text-slate-50 hover:bg-slate-600"
              >
                <PencilSimpleIcon size={16} weight="bold" />
                {ManageRolesContent.actions.edit}
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setIsDeleteOpen(true)}
                className="border-red-500/40 bg-transparent text-red-400 hover:bg-red-500/10 hover:text-red-300"
              >
                <TrashIcon size={16} weight="bold" />
                {ManageRolesContent.actions.delete}
              </Button>
            </div>
          )}
        </div>

        <dl aria-label={ManageRolesContent.detail.overviewLabel}>
          <div className="mb-4 flex items-center gap-2 rounded-lg border border-slate-600 bg-slate-600/50 px-3 py-2.5">
            <KeyIcon
              size={14}
              weight="bold"
              className="shrink-0 text-slate-500"
            />
            <dt className="font-field-42 text-[11px] uppercase text-slate-500">
              {ManageRolesContent.detail.keyLabel}
            </dt>
            <dd>
              <code className="font-mono text-[13px] text-purple-300">
                {role.key}
              </code>
            </dd>
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <div className="rounded-lg border border-slate-600 bg-slate-600 px-4 py-3">
              <dt className="mb-2 font-field-42 text-[11px] uppercase text-slate-500">
                {ManageRolesContent.detail.colorLabel}
              </dt>
              <dd className="flex items-center gap-2 text-body text-slate-50">
                <span
                  aria-hidden="true"
                  className={cn('h-3 w-3 rounded-full', tone.dotClassName)}
                />
                {tone.label}
              </dd>
            </div>
            <div className="rounded-lg border border-slate-600 bg-slate-600 px-4 py-3">
              <dt className="mb-2 font-field-42 text-[11px] uppercase text-slate-500">
                {ManageRolesContent.detail.statusLabel}
              </dt>
              <dd className="flex items-center gap-2 text-body text-slate-50">
                <ShieldCheckIcon
                  size={18}
                  weight="duotone"
                  className={role.is_system ? 'text-blue-300' : 'text-teal-300'}
                />
                {status}
              </dd>
            </div>
          </div>
        </dl>

        <section className="mt-4 rounded-lg border border-slate-600 bg-slate-600">
          <div className="border-b border-slate-550 px-4 py-3">
            <h2 className="text-large text-slate-50">
              {ManageRolesContent.detail.descriptionLabel}
            </h2>
          </div>
          <p className="px-4 py-4 text-body text-slate-300">
            {role.description || ManageRolesContent.detail.noDescription}
          </p>
        </section>

        <p className="mt-4 text-small text-slate-500">{statusDescription}</p>
      </div>
      <DeleteRoleDrawer
        open={isDeleteOpen}
        onOpenChange={setIsDeleteOpen}
        roleName={role.display_name}
        isSubmitting={deleteRoleMutation.isPending}
        onConfirm={handleDeleteConfirm}
      />
      <ToastNotification
        open={toast.open}
        onClose={hideToast}
        title={toast.title}
        description={toast.description}
        variant={toast.variant}
        position="bottom"
      />
    </div>
  );
}
